"use client";

import { motion, MotionConfig } from "motion/react";
import { PropsWithChildren, useId, useState } from "react";

function Item({ header, children, defaultOpen = false }: PropsWithChildren<{ header: string; defaultOpen?: boolean }>) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [hasFocus, setHasFocus] = useState(false);
  const id = useId();

  return (
    <MotionConfig transition={{ duration: 0.3 }}>
      <motion.section initial={false} animate={isOpen ? "open" : "closed"}>
        <h3>
          <motion.button
            id={id + "-button"}
            aria-expanded={isOpen}
            aria-controls={id}
            onClick={() => setIsOpen(!isOpen)}
            onFocus={onlyKeyboardFocus(() => setHasFocus(true))}
            onBlur={() => setHasFocus(false)}
          >
            <span>{header}</span> <ChevronDownIcon />
            {hasFocus && <motion.div layoutId="focus-ring" className="focus-ring" />}
          </motion.button>
        </h3>
        <motion.div
          variants={{
            open: {
              height: "auto",
              maskImage: "linear-gradient(to bottom, black 100%, transparent 100%)",
            },
            closed: {
              height: 0,
              maskImage: "linear-gradient(to bottom, black 50%, transparent 100%)",
            },
          }}
          id={id}
          aria-labelledby={id + "-button"}
          className="accordion-content"
        >
          <motion.div
            variants={{
              open: {
                filter: "blur(0px)",
                opacity: 1,
              },
              closed: {
                filter: "blur(2px)",
                opacity: 0,
              },
            }}
          >
            {children}
          </motion.div>
        </motion.div>
        <hr />
      </motion.section>
    </MotionConfig>
  );
}

export default function FAQ() {
  return (
    <div className="w-full py-20">
      <div className="max-w-4xl mx-auto px-8">
      {/* FAQ Header */}
      <motion.div
        className="text-center mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-bold text-white mb-4">Frequently Asked Questions</h2>
        <p className="text-xl text-white/80">Everything you need to know about Fellou</p>
      </motion.div>

      {/* FAQ Accordion */}
      <motion.div
        className="accordion"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        viewport={{ once: true }}
      >
        <Item header="How is an AI browser different from a chat assistant?" defaultOpen={true}>
          <p>
            An AI chat assistant is reactive, only answering your questions. An AI Browser with agentic AI can
            automatically run redundant work for you and even proactively help you to complete tasks you&apos;ve
            forgotten.
          </p>
          <p><strong>The biggest differences:</strong></p>
          <ul>
            <li>
              <strong>Deep action:</strong> Fellou independently plan and execute complex web and desktop based tasks across multiple apps to achieve your goals.
            </li>
            <li>
              <strong>Deep search:</strong> Fellou automates in-depth research across the entire internet, including logged-in accounts on platforms like X, Reddit or Salefore.
            </li>
            <li>
              <strong>Dynamic Multitasking:</strong> Fellou runs multiple tasks simultaneously in its back-end workspace, while users browse other sites.
            </li>
            <li>
              <strong>Agentic memory:</strong> Fellou can offer proactive, personalized help by connecting browser history, chat history, contextual page data, and personal knowledge base.
            </li>
          </ul>
        </Item>

        <Item header="Can AI browsers do more than just summarize pages and organize tabs?">
          <p>Yes, it goes far beyond basic summarization. An Agentic AI Browser automates complex work across the web.</p>
          <ul>
            <li>Conducts in-depth research to generate reports.</li>
            <li>Automates data entry and social media management.</li>
            <li>Automatically executes custom, multi-step workflows you define to achieve specific goals.</li>
          </ul>
        </Item>

        <Item header="Can AI automate my work across different apps and websites?">
          <p>Yes, cross-platform automation is a core function of an Agentic Browser.</p>
          <ul>
            <li>It executes complex workflows across different apps and websites.</li>
            <li>It can pull data from one platform (like LinkedIn) and use it in another (like Notion).</li>
            <li>This provides a more integrated automation experience than API-based tools.</li>
          </ul>
        </Item>

        <Item header="Does it work on sites that require a login or CAPTCHA?">
          <p>Yes, a key capability of our AI Browser is navigating the real web.</p>
          <ul>
            <li>It is designed to handle logins, authentication, and even CAPTCHA challenges.</li>
            <li>Advanced AI can solve CAPTCHAs with high accuracy by simulating human behavior.</li>
            <li>This enables automation on secure sites.</li>
          </ul>
        </Item>

        <Item header="Can I see and control exactly what the AI agent is doing?">
          <p>Yes, our AI Browser is designed for complete transparency and user control.</p>
          <ul>
            <li>Visually inspect and edit the AI Browser's automatic workflow before it runs.</li>
            <li>Monitor the agent's progress in real-time.</li>
            <li>Intervene at any step to adjust or halt the task, ensuring you always have full oversight.</li>
          </ul>
        </Item>

        <Item header="What can AI Browser create other than answering questions by text?">
          <p>Yes, it's a multi-modal creation tool. Our AI Browser can generate:</p>
          <ul>
            <li>In-depth research reports that include text, images, and charts.</li>
            <li>Creative content such as images, logos, SVGs, and even music.</li>
            <li>Structured documents like presentations (PPTs) and spreadsheets (Excel).</li>
            <li>Code for web pages and applications&mdash;from websites and games to interactive models&mdash;using HTML, CSS, and JavaScript.</li>
          </ul>
        </Item>

        <Item header="Can I schedule tasks to run automatically in the future?">
          <p>Yes, you can schedule any complex workflow to run automatically.</p>
          <ul>
            <li>Go to Fellou home and find tasks. You will see scheduled settings</li>
            <li>Click +New Schedule to set tasks to execute at a specific time or on a recurring basis.</li>
            <li>This goes beyond simple reminders, enabling full delegation of routine digital work. It&apos;s ideal for recurring reports or data monitoring.</li>
          </ul>
        </Item>

        <Item header="Can I build my own custom AI agents and workflows?">
          <p>Yes, Fellou is an extensible platform for developers.</p>
          <ul>
            <li>Use our open-source Eko 2.0 framework to build custom AI agents.</li>
            <li>
              Create powerful, intervenable agents that run within the Fellou AI Browser by using a hybrid of natural
              language and JavaScript.
            </li>
            <li>Tailor automation to your specific needs.</li>
          </ul>
        </Item>
      </motion.div>

      <StyleSheet />
      </div>
    </div>
  );
}

/**
 * ==============   Icons   ================
 */
function ChevronDownIcon() {
  return (
    <motion.svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      variants={{
        open: { rotate: 180 },
        closed: { rotate: 0 },
      }}
    >
      <path d="m6 9 6 6 6-6" />
    </motion.svg>
  );
}

/**
 * ==============   Utils   ================
 */
function onlyKeyboardFocus(callback: () => void) {
  return (e: React.FocusEvent<HTMLButtonElement>) => {
    if (e.type === "focus" && e.target.matches(":focus-visible")) {
      callback();
    }
  };
}

/**
 * ==============   Styles   ================
 */
function StyleSheet() {
  return (
    <style>{`
        .accordion {
            display: flex;
            flex-direction: column;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(12px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            overflow: hidden;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .accordion section {
            padding: 24px;
            position: relative;
        }

        .accordion h3 {
            margin: 0;
            display: flex;
        }

        .accordion hr {
            margin: 0;
            border: 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            position: absolute;
            bottom: 0;
            left: 24px;
            right: 24px;
            z-index: 0;
        }

        .accordion section:last-child hr {
            display: none;
        }

        .accordion h3 button {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex: 1;
            position: relative;
            background: none;
            border: none;
            color: white;
            font-size: 18px;
            font-weight: 600;
            cursor: pointer;
            text-align: left;
            padding: 0;
            outline: none;
        }

        .accordion h3 span,
        .accordion h3 svg {
            text-align: left;
            z-index: 1;
            position: relative;
        }

        .accordion h3 svg {
            color: rgba(255, 255, 255, 0.6);
            flex-shrink: 0;
        }

        .accordion .focus-ring {
            position: absolute;
            inset: -8px;
            background: rgba(59, 130, 246, 0.15);
            border-radius: 8px;
            z-index: 0;
        }

        .accordion-content {
            overflow: hidden;
        }

        .accordion-content > div {
            padding: 20px 0 0;
        }

        .accordion-content p {
            line-height: 1.6;
            padding: 0;
            margin: 0 0 16px 0;
            color: rgba(255, 255, 255, 0.9);
            font-size: 16px;
        }

        .accordion-content ul {
            margin: 0;
            padding: 0 0 0 20px;
            color: rgba(255, 255, 255, 0.8);
        }

        .accordion-content li {
            line-height: 1.6;
            margin-bottom: 8px;
            font-size: 15px;
        }

        .accordion-content strong {
            color: rgba(255, 255, 255, 0.95);
            font-weight: 600;
        }
    `}</style>
  );
}